import { useState, useEffect } from "react";
import { ExpandedContent, Separator } from "../../Compartilhado/styles";
import { IItemHistorico } from "../../../../types";
import { DadosComentario, DadosComentariosMultiplos } from "./types";
import { processarDadosComentario } from "./utils";
import BlocoPrincipal from "./Blocos/BlocoPrincipal/index";
import BlocoEmail from "../DemandaCriada/Blocos/BlocoEmail/index";
import BlocoAlteracoes from "./Blocos/BlocoAlteracoes/index";
import LogsVazio from "../../Compartilhado/LogsVazio";
import api from "../../../../../../../services/api";
import { useHistory, useParams } from "react-router-dom";

interface ExpandedContentProps {
    item: IItemHistorico;
    onNavigateToFile?: (fileId: number) => void;
}

export default function ExpandedContentComentario({ item }: ExpandedContentProps) {
    const [dados, setDados] = useState<DadosComentario | DadosComentariosMultiplos | null>(null);
    const [loading, setLoading] = useState(true);
    const history = useHistory();
    const params = useParams<{ id: string }>();
    const idProposta = params.id ? Number(params.id) : undefined;

    useEffect(() => {
        if (!item.id) {
            setLoading(false);
            return;
        }

        const buscarDados = async () => {
            try {
                setLoading(true);
                const response = await api.get(`log-historico/${item.id}`);

                if (response.data.status === 'success') {
                    const logs = response.data.result || [];
                    const dadosProcessados = processarDadosComentario(logs);
                    setDados(dadosProcessados);
                } else {
                    setDados({
                        campos: [],
                        email: null,
                        idComentario: undefined,
                        idProposta: undefined,
                        idComentarioPai: undefined
                    });
                }
            } catch (error: any) {
                console.error('Erro ao buscar dados do histórico:', error);
                setDados({
                    campos: [],
                    email: null,
                    idComentario: undefined,
                    idProposta: undefined,
                    idComentarioPai: undefined
                });
            } finally {
                setLoading(false);
            }
        };

        buscarDados();
    }, [item.id]);

    const handleVerNoContexto = () => {
        if (!dados) {
            return;
        }
        
        const propostaId = idProposta || (('comentarios' in dados) 
            ? (dados as DadosComentariosMultiplos).comentarios[0]?.idProposta 
            : (dados as DadosComentario).idProposta);
        
        if (!propostaId) {
            return;
        }
        
        const acaoLower = item.acao.toLowerCase().trim();
        const isResposta = acaoLower.includes('resposta adicionada');
        const isEditado = acaoLower.includes('comentário editado') || acaoLower.includes('comentario editado');
        const isSequenciaExcluida = acaoLower.includes('sequência de comentários excluída') || acaoLower.includes('sequencia de comentarios excluida');
        
        let idComentarioPrincipal: number | undefined;
        
        if (isSequenciaExcluida && 'comentarios' in dados) {
            // Para sequência de exclusões, redirecionar para o primeiro comentário (principal)
            const dadosMultiplos = dados as DadosComentariosMultiplos;
            const primeiroComentario = dadosMultiplos.comentarios[0];
            if (primeiroComentario) {
                idComentarioPrincipal = primeiroComentario.idComentarioPai || primeiroComentario.idComentario;
            }
        } else if (!('comentarios' in dados)) {
            const dadosUnicos = dados as DadosComentario;
            // Se for resposta (editada ou não), SEMPRE usar o pai (nunca o ID da resposta)
            // Se for comentário principal editado, usar o próprio ID
            // Se for comentário principal criado, usar o próprio ID
            if (isResposta) {
                // Resposta: sempre usar o pai
                idComentarioPrincipal = dadosUnicos.idComentarioPai;
            } else if (isEditado) {
                // Comentário editado: se tiver pai é resposta editada (usar pai), senão é principal editado (usar próprio ID)
                idComentarioPrincipal = dadosUnicos.idComentarioPai || dadosUnicos.idComentario;
            } else {
                // Comentário criado: usar pai se tiver, senão usar próprio ID
                idComentarioPrincipal = dadosUnicos.idComentarioPai || dadosUnicos.idComentario;
            }
        }
        
        if (idComentarioPrincipal) {
            sessionStorage.setItem('highlightComentario', String(idComentarioPrincipal));
            history.push(`/demandas/${propostaId}/comentarios`);
        }
    };

    if (loading) {
        return (
            <ExpandedContent>
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--c64748b)' }}>
                    Carregando dados...
                </div>
            </ExpandedContent>
        );
    }

    if (!dados) {
        return (
            <ExpandedContent>
                <Separator />
                <LogsVazio />
            </ExpandedContent>
        );
    }

    const acaoLower = item.acao.toLowerCase().trim();
    const isEditado = acaoLower.includes('comentário editado') || acaoLower.includes('comentario editado');
    const isResposta = acaoLower.includes('resposta adicionada');
    const isExcluido = acaoLower.includes('excluído') || acaoLower.includes('excluido') || acaoLower.includes('sequência de comentários excluída') || acaoLower.includes('sequencia de comentarios excluida');
    const isSequenciaExcluida = acaoLower.includes('sequência de comentários excluída') || acaoLower.includes('sequencia de comentarios excluida');

    // Verificar se é múltiplos comentários (sequência de exclusões)
    if (isSequenciaExcluida && dados && 'comentarios' in dados) {
        const dadosMultiplos = dados as DadosComentariosMultiplos;
        
        if (!dadosMultiplos.comentarios || dadosMultiplos.comentarios.length === 0) {
            return (
                <ExpandedContent>
                    <Separator />
                    <LogsVazio />
                </ExpandedContent>
            );
        }
        
        return (
            <ExpandedContent>
                <Separator />
                <div className="partes-container">
                    {dadosMultiplos.comentarios.map((comentario, index) => {
                        const camposAlteracoes = comentario.campos ? comentario.campos.filter(c => c.campo.includes('Antes') || c.campo.includes('Depois')) : [];
                        const temAlteracoes = camposAlteracoes.length > 0;
                        
                        return (
                            <div key={comentario.idComentario || index} className="parte-item">
                                {comentario.idComentario && (
                                    <BlocoPrincipal 
                                        campos={[]} 
                                        idComentario={comentario.idComentario}
                                        onVerNoContexto={undefined}
                                        esconderTabela={true}
                                    />
                                )}
                                {temAlteracoes && camposAlteracoes.length > 0 && (
                                    <BlocoAlteracoes campos={camposAlteracoes} esconderTitulo={true} />
                                )}
                            </div>
                        );
                    })}
                </div>
                {dadosMultiplos.email && <BlocoEmail email={dadosMultiplos.email} />}
            </ExpandedContent>
        );
    }

    // Caso único comentário (comportamento original)
    const dadosUnicos = dados as DadosComentario;
    
    if (!dadosUnicos.campos || (dadosUnicos.campos.length === 0 && dadosUnicos.email === null)) {
        return (
            <ExpandedContent>
                <Separator />
                <LogsVazio />
            </ExpandedContent>
        );
    }
    
    const camposPrincipais = dadosUnicos.campos.filter(c => !c.campo.includes('Antes') && !c.campo.includes('Depois'));
    const camposAlteracoes = dadosUnicos.campos.filter(c => c.campo.includes('Antes') || c.campo.includes('Depois'));
    const temAlteracoes = dadosUnicos.campos.some(c => c.campo.includes('Antes') || c.campo.includes('Depois'));

    // Sempre mostrar o ID do comentário que está sendo exibido no log (o próprio comentário editado/criado/excluído)
    const idComentarioParaExibir = dadosUnicos.idComentario;

    return (
        <ExpandedContent>
            <Separator />
            {idComentarioParaExibir && (
                <BlocoPrincipal 
                    campos={(isEditado || isExcluido) ? [] : camposPrincipais} 
                    idComentario={idComentarioParaExibir}
                    onVerNoContexto={isExcluido ? undefined : handleVerNoContexto}
                    esconderTabela={isEditado || isExcluido}
                />
            )}
            {temAlteracoes && camposAlteracoes.length > 0 && (
                <BlocoAlteracoes campos={camposAlteracoes} esconderTitulo={isEditado || isExcluido} />
            )}
            {dadosUnicos.email && <BlocoEmail email={dadosUnicos.email} />}
        </ExpandedContent>
    );
}

